/**
 * Adapters between the scenario data model and React Flow's runtime shape.
 *
 * Pure functions. The canvas component re-runs these whenever the scenario
 * changes; they're cheap enough at prototype scale that no memoization is
 * needed yet. If a future scenario has hundreds of nodes, this is where to
 * profile first.
 *
 * The mapping:
 *   - One React Flow node per scenario node. The `data` payload carries the
 *     full ScenarioNode, plus a validation issue summary computed once per
 *     re-render, so the custom node component renders without re-touching
 *     the store.
 *   - One React Flow edge per (choice, destination) pair. The edge id encodes
 *     the (nodeId, choiceId, destinationIndex) tuple so React Flow can
 *     identify it. Conditional destinations get a dashed stroke.
 */
import type { Edge, Node } from '@xyflow/react';
import type { Scenario, ScenarioNode, ValidationResult, Issue } from '@branchworks/shared';

/** Data carried inside each React Flow node. */
export interface ScenarioNodeData extends Record<string, unknown> {
  /** The full underlying node, so the custom component can render everything. */
  node: ScenarioNode;
  /** Whether this is the scenario's start node. Affects header text. */
  isStart: boolean;
  /** Validation issues whose path.nodeId matches this node. */
  issues: Issue[];
  /** Convenience: errors and warnings split out. */
  errorCount: number;
  warningCount: number;
  /** The whole scenario, so the custom node can render objective dots. */
  scenario: Scenario;
}

export type ScenarioFlowNode = Node<ScenarioNodeData>;

/** Convert a scenario to a list of React Flow nodes. */
export function scenarioToFlowNodes(
  scenario: Scenario,
  validation: ValidationResult,
): ScenarioFlowNode[] {
  return Object.values(scenario.nodes).map((node) => {
    const issues = validation.byNodeId[node.id] || [];
    const errorCount = issues.filter((i) => i.level === 'error').length;
    const warningCount = issues.filter((i) => i.level === 'warning').length;
    return {
      id: node.id,
      type: 'scenario',
      position: { x: node.x, y: node.y },
      // dragHandle defaults to whole-node, which is what we want — clicking
      // anywhere on the card both selects and starts a drag.
      data: {
        node,
        isStart: node.id === scenario.startNodeId,
        issues,
        errorCount,
        warningCount,
        scenario,
      },
    };
  });
}

/**
 * Convert a scenario's choice→destination relationships into React Flow edges.
 *
 * The edge id format `{nodeId}::{choiceId}::{destIndex}` makes the edge
 * uniquely addressable, which matters once edge deletion lands.
 *
 * Edges with `condition !== null` (conditional destinations) get rendered
 * dashed; unconditional gets solid. This is the same visual treatment the
 * prototype's hand-rolled SVG arrows used.
 */
export function scenarioToFlowEdges(scenario: Scenario): Edge[] {
  const edges: Edge[] = [];
  for (const node of Object.values(scenario.nodes)) {
    for (const choice of node.choices) {
      const dests = choice.destinations || [];
      dests.forEach((dest, di) => {
        if (!dest.destinationId) return;
        if (!scenario.nodes[dest.destinationId]) return;
        const isConditional = dest.condition !== null && dest.condition !== undefined;
        edges.push({
          id: `${node.id}::${choice.id}::${di}`,
          source: node.id,
          // sourceHandle encodes which choice the edge comes from, so multiple
          // choices on one node fan out cleanly. The Handle on the node card
          // is rendered with this same id.
          sourceHandle: choice.id,
          target: dest.destinationId,
          // No targetHandle — every node has a single top-center target handle.
          style: {
            stroke: '#999388',
            strokeWidth: 1.2,
            strokeDasharray: isConditional ? '4 3' : undefined,
          },
          // React Flow renders an arrowhead at the target end via the marker spec.
          markerEnd: { type: 'arrowclosed', color: '#999388', width: 14, height: 14 } as never,
        });
      });
    }
  }
  return edges;
}
